import { Info, X } from 'lucide-react';
import {
  markPolyGateBannerDismissed,
  usePolyGateBannerStore,
} from '../stores/polyGateBannerStore';

/**
 * One-time dismissable banner shown to a trader-role user whose browser had
 * stale personal-key Polymarket credentials when the role gate landed.
 *
 * Flow:
 *  1. AuthedShell mounts, detects role === 'trader' + non-empty saved poly
 *     config in localStorage.
 *  2. It calls `usePolyAccountStore.getState().removeCredentials()` to wipe
 *     the storage + in-memory state, then sets `showCleared: true` on the
 *     banner store (only if the user hasn't dismissed before).
 *  3. This component reads `showCleared` and renders. On dismiss it persists
 *     the dismissed flag and flips the store back to hidden.
 *
 * Visual matches the KeyModeBanner palette so it slots in cleanly above
 * trader views.
 */
export function PolyPersonalKeyClearedBanner() {
  const show = usePolyGateBannerStore((s) => s.showCleared);
  const setShow = usePolyGateBannerStore((s) => s.setShowCleared);
  if (!show) return null;

  const handleDismiss = () => {
    markPolyGateBannerDismissed();
    setShow(false);
  };

  return (
    <div
      role="status"
      className="flex items-center gap-2 border-b border-amber-500/20 bg-amber-500/5 px-4 py-1.5 text-[11px] text-amber-700 dark:text-amber-300"
    >
      <Info size={12} aria-hidden="true" />
      <span className="flex-1">
        Personal Polymarket trading is disabled for trader accounts in this org.
        Your saved credentials have been cleared from this browser.
      </span>
      <button
        onClick={handleDismiss}
        title="Dismiss"
        aria-label="Dismiss notice"
        className="rounded p-0.5 text-amber-700 hover:bg-amber-500/20 dark:text-amber-300"
      >
        <X size={12} />
      </button>
    </div>
  );
}
